import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Typography } from '@mui/material';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import useImage from 'services/Hooks/useImage';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import { ExteriorSignageQuoteAttachedFileTypeEnum } from 'enums/ExteriorSignageQuoteAttachedFileTypeEnum';
import ExteriorSignageQuoteUploadFileSection from './ExteriorSignageQuoteUploadFileSection';

const useStyles = makeStyles(theme => ({
  pageFull: {
    width: '100%',
    marginTop: '1vh',
  },
  noItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  totalInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-end',
    alignItems: 'flex-end',
  },
  uploadBox: {
    backgroundColor: '#f5f5f5',
    padding: '10px 15px',
    borderRadius: '8px',
    marginTop: '10px',
    width: '100%',
  },
}));

const ExteriorSignageQuoteInfoCheckoutSection = ({ exteriorSignageQuote, isLoading, isDisabled, hasAttemptedCheckout }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { getExteriorSignageImage } = useImage();

  const legacyFiles = exteriorSignageQuote?.files?.filter(f => f.exteriorSignageQuoteItem_Id == null) || [];
  const isLegacyQuote = legacyFiles.length > 0;

  return (
    <Box className={classes.pageFull}>
      {exteriorSignageQuote &&
        exteriorSignageQuote?.quoteItems?.map(item => {
          const requiredOem = isLegacyQuote
            ? 0
            : item.allowOEM && item.quantityOEM > 0
            ? item.quantityOEM * item.quantity
            : 0;
          const requiredLogo = isLegacyQuote ? 0 : item.allowDealerLogo ? 1 : 0;

          const uploadedOem =
            exteriorSignageQuote?.files?.filter(
              f =>
                f.exteriorSignageQuoteType === ExteriorSignageQuoteAttachedFileTypeEnum.OEM &&
                f.exteriorSignageQuoteItem_Id === item.id,
            ).length || 0;

          const uploadedLogo = isLegacyQuote
            ? legacyFiles.length
            : exteriorSignageQuote?.files?.filter(
                f =>
                  f.exteriorSignageQuoteType === ExteriorSignageQuoteAttachedFileTypeEnum.Logo &&
                  f.exteriorSignageQuoteItem_Id === item.id,
              ).length || 0;

          const hasOemError = hasAttemptedCheckout && requiredOem > 0 && uploadedOem !== requiredOem;
          const hasLogoError = hasAttemptedCheckout && requiredLogo > 0 && uploadedLogo === 0;

          const showLogoSection = requiredLogo > 0 || isLegacyQuote;
          const showOemSection = requiredOem > 0;

          return (
            <GridContainer key={item.id} style={{ marginBottom: '1rem' }}>
              <GridItem xs={12} md={2}>
                <Box className={classes.center}>
                  <img
                    src={
                      item?.imgSrc
                        ? getExteriorSignageImage(item?.exteriorSignage_Id, item?.imgSrc.id)
                        : '/images/brp-logo.png'
                    }
                    alt="img"
                    style={{ maxWidth: '10vh', maxHeight: '10vh' }}
                  />
                </Box>
              </GridItem>
              <GridItem xs={12} md={7}>
                <Typography variant="h2">{item.name}</Typography>
                <Typography variant="h5" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                  {item?.sku}
                </Typography>
                <Divider sx={{ mt: 0 }} />
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h2">{t('unitPrice')}</Typography>
                  <Typography fontSize={25} color={'text.primary'} fontWeight="bold">
                    ${item?.unitPrice?.toFixed(2)} {item.currencyName}
                  </Typography>
                </Box>
                <Divider sx={{ mt: 0, mb: item.unitPricePromotion !== item.unitPrice ? 2 : 0 }} />
              </GridItem>
              <GridItem xs={12} md={3} sx={{ alignSelf: 'center' }}>
                <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
                  <Typography variant="h2">{t('quantity')}</Typography>
                </Box>
                <Box display="flex" justifyContent={'center'} alignItems={'center'}>
                  <Typography variant="h1">{item.quantity}</Typography>
                </Box>
              </GridItem>

              {(showLogoSection || showOemSection) && (
                <GridItem xs={12}>
                  <Box className={classes.uploadBox}>
                    <GridContainer spacing={2}>
                      {showLogoSection && (
                        <GridItem xs={12} md={showOemSection ? 6 : 12}>
                          <ExteriorSignageQuoteUploadFileSection
                            exteriorSignageQuote={exteriorSignageQuote}
                            itemId={item.id}
                            isDisabled={isDisabled}
                            isLoading={isLoading}
                            fileType={ExteriorSignageQuoteAttachedFileTypeEnum.Logo}
                            maxFiles={isLegacyQuote ? legacyFiles.length : requiredLogo}
                            titleText={t('upload_logo')}
                            formatText={t('file_format')}
                            dropzoneText={t('drag_and_drop_logo')}
                            hasError={hasLogoError}
                            isLegacyQuote={isLegacyQuote}
                          />
                          {hasLogoError && (
                            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                              {t('missing_logo_error')}
                            </Typography>
                          )}
                        </GridItem>
                      )}

                      {showOemSection && (
                        <GridItem xs={12} md={showLogoSection ? 6 : 12}>
                          <ExteriorSignageQuoteUploadFileSection
                            exteriorSignageQuote={exteriorSignageQuote}
                            itemId={item.id}
                            isDisabled={isDisabled}
                            isLoading={isLoading}
                            fileType={ExteriorSignageQuoteAttachedFileTypeEnum.OEM}
                            maxFiles={requiredOem}
                            titleText={t('upload_oem_images')}
                            formatText={t('file_format')}
                            dropzoneText={t('drag_and_drop_oem_images')}
                            hasError={hasOemError}
                            isLegacyQuote={false}
                          />
                          {hasOemError && (
                            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                              {t('missing_oem_images_error')}
                            </Typography>
                          )}
                        </GridItem>
                      )}
                    </GridContainer>
                  </Box>
                </GridItem>
              )}

              <GridItem xs={12}>
                <Divider sx={{ mt: 2 }} />
              </GridItem>
            </GridContainer>
          );
        })}

      {!exteriorSignageQuote ||
        (exteriorSignageQuote.quoteItems?.length <= 0 && (
          <Box className={classes.noItem}>
            <Typography fontSize={20} color="text.secondary" sx={{ mt: 1, mb: 1 }}>
              {t('NoItemInExteriorSignageQuote')}
            </Typography>
          </Box>
        ))}

      <SkeletonWrapper
        skeletonProps={{ width: 210, height: 33 }}
        isLoading={isLoading}
        component={
          <Box className={classes.totalInfo} sx={{ mt: 2 }}>
            <Typography variant="h1">${(exteriorSignageQuote?.total ?? 0).toFixed(2)}</Typography>
          </Box>
        }
      />
    </Box>
  );
};

export default ExteriorSignageQuoteInfoCheckoutSection;
